import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();

  if (!open) return null;

  return (
    <>
      <div className="cart-overlay" onClick={() => setOpen(false)} data-testid="cart-overlay" />
      <aside className="cart-drawer" data-testid="cart-drawer">
        <div className="flex items-center justify-between px-8 py-6 border-b" style={{ borderColor: "var(--line)" }}>
          <div>
            <p className="tracking-overline text-[10px] uppercase opacity-60">Your Bag</p>
            <h2 className="font-display text-2xl mt-1">{items.length} {items.length === 1 ? "piece" : "pieces"}</h2>
          </div>
          <button onClick={() => setOpen(false)} aria-label="close" data-testid="cart-close-button">
            <X size={22} strokeWidth={1.2} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display italic text-xl">Your bag awaits.</p>
              <p className="text-sm mt-3 opacity-60">Begin curating your collection.</p>
              <button
                className="btn-outline mt-8"
                onClick={() => setOpen(false)}
                data-testid="cart-empty-shop-button"
              >
                Explore the Collection
              </button>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map((item) => (
                <li
                  key={`${item.product_id}-${item.size}`}
                  className="flex gap-4 pb-6 border-b"
                  style={{ borderColor: "var(--line-soft)" }}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="w-24 h-32 overflow-hidden flex-shrink-0" style={{ background: "var(--line-soft)" }}>
                    {item.image && (
                      <img src={item.image} alt={item.name} className="object-cover w-full h-full" />
                    )}
                  </div>
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-display text-lg leading-tight">{item.name}</p>
                        <p className="text-[11px] uppercase tracking-[0.18em] opacity-60 mt-1">Size {item.size}</p>
                      </div>
                      <button
                        onClick={() => removeItem(item.product_id, item.size)}
                        aria-label="remove"
                        className="opacity-50 hover:opacity-100"
                        data-testid={`cart-remove-${item.product_id}`}
                      >
                        <Trash2 size={15} strokeWidth={1.2} />
                      </button>
                    </div>
                    <div className="mt-auto flex justify-between items-center">
                      <div className="flex items-center border" style={{ borderColor: "var(--line)" }}>
                        <button
                          className="px-2 py-1"
                          onClick={() => updateQty(item.product_id, item.size, item.quantity - 1)}
                          data-testid={`cart-qty-decrease-${item.product_id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                        <button
                          className="px-2 py-1"
                          onClick={() => updateQty(item.product_id, item.size, item.quantity + 1)}
                          data-testid={`cart-qty-increase-${item.product_id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <p className="font-display text-lg">{formatPrice(item.price * item.quantity, item.currency)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t px-8 py-6" style={{ borderColor: "var(--line)" }}>
            <div className="flex justify-between items-baseline mb-2">
              <span className="tracking-overline text-[11px] uppercase">Subtotal</span>
              <span className="font-display text-2xl" data-testid="cart-subtotal">{formatPrice(subtotal, items[0]?.currency)}</span>
            </div>
            <p className="text-xs opacity-60 mb-5">Shipping & taxes calculated at checkout.</p>
            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              className="btn-primary w-full"
              data-testid="cart-checkout-button"
            >
              Proceed to Checkout
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="btn-ghost w-full mt-4"
              data-testid="cart-continue-shopping-button"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
